<template>

<v-dialog v-model="show" @hidden="onHidden" id="unwrap-eth" :fullscreen="fullscreen" max-width="600px" persistent>

	<v-card>

		<!--Modal Title -->
		<v-toolbar flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
			<v-toolbar-title>{{ $t('compAddTokenTracking.title') }}</v-toolbar-title>
			<v-spacer></v-spacer>
			<v-btn icon @click="show = false">
				<v-icon>mdi-window-close</v-icon>
			</v-btn>
		</v-toolbar>

		<!-- Modal Contents -->
		<v-card-text class="pa-5">

			<v-select v-if="!input.tokenPreSelected"
				v-model.trim="input.addTokenMethod"
				:items="addTokenMethodOptions"
				solo
				label="Add Token by:"
				style="margin-bottom: -10px"
			></v-select>

			<!-- select from verified tokens -->
			<div v-if="input.addTokenMethod === 'select'">

				<div class="mb-3">{{ $t('compAddTokenTracking.optSelectIntro') }}</div>

				<v-card v-if="tokenOptions.length == 0" class="bg-gray pa-3 mb-3">
					 {{ $t('compAddTokenTracking.optSelectNoMore') }}
				</v-card>

				<v-card v-else class="mb-3" elevation="2">

					<v-card-text style="max-height: 200px; overflow-y: scroll;">

						<div v-for="token of tokenOptions"
							:key="token.address"
							class="py-2"
							:class="{ 'bg-green': input.token === token }"
						>

							<!-- Select Box -->
							<span class="ml-2 mr-3">
								<v-icon v-if="input.token === token"
									color="success"
									@click="unSelectToken()">mdi-checkbox-marked</v-icon>
								<v-icon v-else
									color="gray"
									@click="selectToken(token)">mdi-checkbox-blank-outline</v-icon>
							</span>

							<!-- Token Symbol -->
							<ERC20Token
								:name="token.name"
								:symbol="token.symbol"
								:decimals="token.decimals"
								:address="token.address"
								:tokenurl="networkConfig.tokenURL"
							/>

							<!-- Token Name -->
							<span class="ml-3">{{ token.name }}</span>

						</div>

					</v-card-text><!-- v-card-text -->

				</v-card><!--v-card -->

				<!-- Add token button -->
				<div class="text-center my-3">
					<v-btn color="primary" @click="addTokenTracking()" :disabled="!input.token || adding">
						{{ $t('compAddTokenTracking.btnTxtAddToken') }}
						<span v-if="input.token">: {{ input.token.symbol }}</span>
						<ButtonLoading v-show="adding" class="ml-3" />
					</v-btn>
				</div>

			</div>

			<!-- Search token -->
			<div v-if="input.addTokenMethod === 'search'">

				<v-form ref="search-token-form" v-if="!input.tokenPreSelected"
					v-model="input.tokenSearchFormValid">
					<v-text-field
						outlined color="gray" persistent-hint
						:label="$t('compAddTokenTracking.optSearchLabel')"
						id="token-address-search"
						v-model.trim="input.tokenSearchAddress"
						:rules="rules.tokenSearchAddress"
						type="text"
						clearable
						placeholder=""
						:hint="$t('compAddTokenTracking.optSearchTxtExample') + ': ' + exampleTokenAddress"
						required
					></v-text-field>

					<div class="text-center my-3">
						<v-btn color="primary" :disabled="!input.tokenSearchFormValid" @click="searchTokenByAddress()">
							{{ $t('compAddTokenTracking.btnTxtSearchToken') }} <ButtonLoading v-show="searching" class="ml-3" />
						</v-btn>
					</div>

				</v-form>

				<!-- Token Found -->
				<div v-if="searchDone === 'found'" v-bind:class="{'green lighten-5': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }" class="pa-4 my-2">

					<div v-if="tokenAlreadyAdded(input.tokenSearchAddress)">
						{{ this.$t('compAddTokenTracking.searchErrAlreadyAdded') }}
					</div>

					<div v-else>

						<div class="text-center title" v-if="!input.tokenPreSelected"><strong>{{ $t('compAddTokenTracking.optSearchResultFoundTitle') }}</strong></div>
						<div><strong>{{ $t('common.token.name') }}</strong>: {{ tokenSearchResult.name }}</div>
						<div><strong>{{ $t('common.token.symbol') }}</strong>: {{ tokenSearchResult.symbol }}</div>
						<div>
							<strong>{{ $t('common.token.address') }}</strong>: {{ input.tokenSearchAddress }}
							<a class="ml-1" :href="config.network[network.id].tokenURL + input.tokenSearchAddress" target="_blank" style="text-decoration: none;"><v-icon>mdi-open-in-new</v-icon></a>
						</div>
						<div><strong>{{ $t('common.token.decimals') }}</strong>: {{ tokenSearchResult.decimals }}</div>

						<div v-bind:class="{'green lighten-4': !$vuetify.theme.dark, 'grey darken-3': $vuetify.theme.dark }" class="mt-2 pa-2">
		            <v-icon v-if="input.agreementChecked" color="primary" @click="toggleAgreement">
		            	mdi-checkbox-marked
		            </v-icon>
		            <v-icon v-else aria-hidden="true" @click="toggleAgreement">
		            	mdi-checkbox-blank-outline
		            </v-icon>
		            {{ $t('compAddTokenTracking.optSearchResultFoundAgreement') }}
						</div>

						<div class="text-center my-3">
							<v-btn color="primary" :disabled="adding || !input.agreementChecked" @click="addSearchedToken()">
								{{ $t('compAddTokenTracking.btnTxtAddToken') }}
								<span v-if="tokenSearchResult.symbol">: {{ tokenSearchResult.symbol }}</span>
								<ButtonLoading v-show="adding" class="ml-3" />
							</v-btn>
						</div>

					</div>

				</div>

				<!-- Token Not Found -->
				<div v-show="searchDone === 'not found'" class="p-3 my-2 bg-gray color-red">
					 {{ $t('compAddTokenTracking.optSearchResultNotFoundTxt') }}
				</div>

			</div>

		</v-card-text>

		<v-card-actions v-if="fullscreen">
			<v-spacer></v-spacer>
			<v-btn color="primary" outlined text class="mr-3" @click="show = false">
				<v-icon>mdi-window-close</v-icon> {{ $t('common.verb.close')}}
			</v-btn>
		</v-card-actions>

	</v-card>
</v-dialog>

</template>

<script>
import { config } from '@/config'
import {mapActions, mapGetters, mapState} from 'vuex'
import ButtonLoading from "@/components/ButtonLoading"
import ERC20Token from "@/components/ERC20Token"
import { Web3Wrapper } from '@0x/web3-wrapper'

export default {
	name: 'AddTokenTracking',
	props: {
		fullscreen: { type: Boolean, default: false},
	},
	components: {
		ButtonLoading,
		ERC20Token
	},
	data () {
		return {
			config: config,
			input: {
				tokenPreSelected: false,
				addTokenMethod: 'select',
				token: null,
				tokenSearchAddress: null,
				tokenSearchFormValid: true,
				agreementChecked: false,
			},
			addTokenMethodOptions: [
				{
					text: this.$t('compAddTokenTracking.optSelectTitle'),
					value: 'select'
				},
				{
					text: this.$t('compAddTokenTracking.optSearchTitle'),
					value: 'search'
				}
			],
			rules: {
				tokenSearchAddress: [
					// Field is required
					val => (val !== null && val !== undefined && val !== '') || this.$t('common.phrase.fieldIsRequired'),
					// Must be a valid ERC20 address
					val => Web3Wrapper.isAddress(val) || this.$t('compAddTokenTracking.searchErrInvalidAddress'),
					// Must not been added already
					val => !this.tokenAlreadyAdded(val) || this.$t('compAddTokenTracking.searchErrAlreadyAdded'),
				]
			},
			tokenSearchResult: {},
			tokenOptions: [],
			exampleTokenAddress: null,
			searchDone: null,
			adding: false,
			searching: false,
			show: false
		}
	},
	computed: {
		...mapGetters({
			network: 'getNetwork',
			account: 'getAccount',
			tokenInfo: 'getTokenInfo',
			myAddress: 'getUserAccount',
			web3Wrapper: 'getWeb3Wrapper',
			providerEngine: 'getProviderEngine',
			contractWrappers: 'getContractWrappers',
			contractAddresses: 'getContractAddresses',
			networkConfig: 'getNetworkConfig'
		}),
	},
	methods: {
		...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount', 'getTokenInfoByAddress']),
		loadTokenOptions () {
			this.input.token = null
			this.input.tokenSearchAddress = null
			this.tokenOptions = []
			this.tokenSearchResult = {}
			this.searchDone = null
			// this.searchFound = false
			const trackedAddress = {}
			for (let token of this.account.trackedTokens) {
				trackedAddress[token.address] = true
			}
			for (let token of this.tokenInfo.verifiedTokenList) {
				if (!trackedAddress[token.address]) {
					this.tokenOptions.push(token)
				}
			}
			this.tokenOptions.sort((a,b) => {
				return a.symbol > b.symbol ? 1 : a.symbol < b.symbol ? -1 : 0;
			})
		},
		async showForm () {
			const WETHToken = this.tokenInfo.WETHToken
			if (!WETHToken) {
				throw new Error ("Cannot get token info for WETH")
			}
			this.exampleTokenAddress = WETHToken.address
			this.adding = false
			this.searchDone = null
			this.loadTokenOptions()
			this.input.tokenPreSelected = false
			this.input.token = null
			this.input.tokenSearchAddress = null
			this.input.agreementChecked = false
			this.input.tokenSearchFormValid = true
			if (this.tokenOptions.length > 0) {
				this.input.addTokenMethod = 'select'
			}
			else {
				this.input.addTokenMethod = 'search'
			}
			this.show = true
		},
		async showFormWithToken (token) {
			if (!token.address || !token.name || !token.symbol || token.decimals == null) {
				throw new Error ("Missing token info")
			}
			this.input.tokenPreSelected = true
			this.input.addTokenMethod = 'search'
			this.input.tokenSearchAddress = token.address
			this.show = true
			this.searchTokenByAddress()
			// this.tokenSearchResult = { ...token }
			// this.searchDone = 'found'
		},
		tokenAlreadyAdded (tokenAddress) {
			if (!tokenAddress) {
				return false
			}
			for (let token of this.account.trackedTokens) {
				if (token.address.toLowerCase() === tokenAddress.toLowerCase()) {
					return true
				}
			}
			return false
		},
		selectToken (token) {
			this.input.token = token
		},
		unSelectToken () {
			this.input.token = null
		},
		onHidden () {
			this.$emit('add-token-tracking-modal-hidden')
		},
		async addTokenTracking () {
			this.adding = true
			const apiURL = `${this.config.backendAPIURL}/${this.network.id}/token-tracking/add/${this.network.userAccount}`
			const newToken = this.input.token
			const apiData = {
				token: newToken
			}
			try {
				await this.$http.post(apiURL, apiData)
				await this.loadAccount(true)
				this.$emit('token-tracking-added-new', newToken)
				this.$parent.$emit("successNotification", this.$t('compAddTokenTracking.successMessage', {tokenSymbol: this.input.token.symbol }))
			} catch (err) {
				console.log(err)
			} finally {
				this.adding = false
				this.show = false
			}
		},
		toggleAgreement () {
			this.input.agreementChecked = !this.input.agreementChecked
		},
		async addSearchedToken () {
			this.input.token = {
				address: this.input.tokenSearchAddress.toLowerCase(),
				name: this.tokenSearchResult.name,
				symbol: this.tokenSearchResult.symbol,
				decimals: this.tokenSearchResult.decimals
			}
			await this.addTokenTracking()
		},
		async searchTokenByAddress () {
			// console.log('searchTokenByAddress')
			this.tokenSearchResult = null
			this.searchDone = null
			this.searching = true
			const result = await this.getTokenInfoByAddress(this.input.tokenSearchAddress)
			if (result && result.name && result.symbol) {
				this.tokenSearchResult = { ...result }
				// console.log('result', this.tokenSearchResult)
				this.searchDone = 'found'
			}
			else {
				this.searchDone = 'not found'
				this.tokenSearchResult = null
			}
			this.searching = false
		}
	},
	created () {
	}
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
</style>
